import React, { useState, useContext } from 'react';
import CustomToolbarButton from './toolbar/custom-toolbar-button';
import ASEmailIcon from '../../../assets/icons/Email.svg';
// import ActionDialog from './action-dialog';
import { useSendEmail } from './use-send-email';
import ValidationUtils from '../../../react-core-lib/utils/validation-utils';
import mixpanel from 'mixpanel-browser';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import StoreContext from '../../../react-core-lib/components/app/store-context';

import BlockingSpinner from '../../UIControls/BlockingSpinner';
import PatientDetailsDialog from './patient-details-dialog';
import { notifyAuthExpired, isAuthExpiredDialogVisible, isAuthExpiredError } from '../../../utils/auth-error-utils';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import ClinikoApi from '../../../rest-api/cliniko-api';
import { getClinikoPatientIdFromStorage } from '../../helper/clinikoParamsHelper';

function HandoutEmailButton({
    handoutData,
    currentUser,
    bundledHandoutsView = false,
    accountType,
    onUpgradeRequired,
    toggleStates,
    generateLink,
    shouldShowPatientDialog = () => false,
}) {
    const INTEGRATION_SPINNER_MESSAGES = {
        cliniko: 'Connecting to Cliniko. Please wait.',
    };
    const { userDataStore, authStore } = useContext(StoreContext);
    const { hasUnsavedChanges, promptBeforeAction } = useUnsavedChanges();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = useState('Email failed to send');
    const [errorDialogMessage, setErrorDialogMessage] = useState('Please try again');
    const [showReloadAction, setShowReloadAction] = useState(false);
    // Patient details dialog state
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingPatientDetails, setPendingPatientDetails] = useState(null);
    const [clinikoPatient, setClinikoPatient] = useState(null);
    const [clinikoAppointments, setClinikoAppointments] = useState([]);
    const [clinikoLoading, setClinikoLoading] = useState(false);
    const [clinikoMeta, setClinikoMeta] = useState(null);
    const [clinikoNoteStatus, setClinikoNoteStatus] = useState('idle'); // idle | added | forbidden | failed
    const [spinnerMessage, setSpinnerMessage] = useState('');
    const [integrationSource, setIntegrationSource] = useState(null);

    const sendEmail = useSendEmail();
    // const theme = useTheme();

    const openAuthAwareErrorDialog = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'handout-email' });
            return;
        }
        setErrorDialogTitle('Email failed to send');
        setErrorDialogMessage('Please try again');
        setShowReloadAction(false);
        setOpenErrorDialog(true);
    };

    const handleDialogSend = async () => {
        try {
            setLoading(true);
            setClinikoNoteStatus('idle');
            const shareContext = integrationSource
                ? { sharedViaIntegration: true, integrationSource }
                : null;
            // Generate the linkId with patient details if available
            const linkId = await generateLink(pendingPatientDetails, shareContext);
            console.log('>>> Generated linkId:', linkId);
            const clinicId = userDataStore.userId;

            const result = await sendEmail(
                email,
                bundledHandoutsView ? 'your handouts' : handoutData.contentName,
                linkId,
                clinicId,
                toggleStates,
            );

            // Assuming successful response means the email is sent
            if (result) {
                mixpanel.track('HandoutShared', {
                    email: currentUser.attributes.email,
                    shareMethod: 'Send Email',
                    handoutTitle: handoutData.contentName,
                });

                if (clinikoMeta?.patientId && clinikoMeta?.bookingId) {
                    try {
                        const templatesResponse = await ClinikoApi.getTreatmentNoteTemplates(authStore.idToken);
                        const templateId = templatesResponse?.defaultTemplateId || null;
                        if (templateId) {
                            const url = new URL(process.env.REACT_APP_PATIENT_HANDOUT_URL);
                            url.searchParams.append('linkId', linkId);
                            url.searchParams.append('clinicId', clinicId);

                            if (toggleStates) {
                                Object.entries(toggleStates).forEach(([key, value]) => {
                                    if (value === false || value === 'false') {
                                        url.searchParams.append(key, 'false');
                                    }
                                });
                            }

                            const handoutTitle = bundledHandoutsView ? 'your handouts' : handoutData.contentName;
                            const sharedLink = url.toString();
                            const now = new Date();
                            const datePart = now.toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                            });
                            const timePart = now
                                .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
                                .replace(' am', 'am')
                                .replace(' pm', 'pm');
                            const displayDate = `${datePart}, ${timePart}`;
                            const noteAnswer = `<p><b>Handout shared via Cetra Health</b></p><p>Handout: ${handoutTitle}</p><p>Link: <a href="${sharedLink}" target="_blank" rel="noopener noreferrer">View Handout</a></p><p>Date: ${displayDate}</p>`;

                            const clinikoResult = await ClinikoApi.addNote(authStore.idToken, {
                                patient_id: clinikoMeta.patientId,
                                booking_id: clinikoMeta.bookingId,
                                treatment_note_template_id: templateId,
                                draft: false,
                                content: {
                                    sections: [
                                        {
                                            questions: [
                                                {
                                                    name: 'Patient progress report',
                                                    type: 'paragraph',
                                                    answer: '',
                                                },
                                                {
                                                    name: 'Assessment',
                                                    type: 'paragraph',
                                                    answer: '',
                                                },
                                                {
                                                    name: 'Treatment',
                                                    type: 'paragraph',
                                                    answer: noteAnswer,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            });
                            if (clinikoResult?.success) {
                                setClinikoNoteStatus('added');
                            } else if (clinikoResult?.statusCode === 403 || clinikoResult?.reason === 'forbidden') {
                                setClinikoNoteStatus('forbidden');
                            } else {
                                setClinikoNoteStatus('failed');
                            }
                        } else {
                            console.warn('[Cliniko] No default treatment note template found.');
                            setClinikoNoteStatus('failed');
                        }
                    } catch (e) {
                        console.warn('[Cliniko] Failed to add note:', e?.message || e);
                        setClinikoNoteStatus('failed');
                    }
                }

                setOpenSuccessDialog(true);
                // Clear pending patient details after successful send
                setPendingPatientDetails(null);
                setClinikoMeta(null);
            } else {
                openAuthAwareErrorDialog();
            }
        } catch (error) {
            console.error('Error while sending email:', error);
            openAuthAwareErrorDialog(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePatientDetailsSubmit = async (patientDetails, meta = {}) => {
        // Save patient details and proceed to email dialog
        setPendingPatientDetails(patientDetails);
        if (meta?.cliniko) {
            setClinikoMeta(meta.cliniko);
            setIntegrationSource(meta.integrationSource || null);
            if (meta.cliniko.patientEmail) {
                setEmail(meta.cliniko.patientEmail);
            }
        } else if (patientDetails?.patientEmail) {
            setClinikoMeta(null);
            setIntegrationSource(meta?.integrationSource || null);
            setEmail(patientDetails.patientEmail);
        } else {
            setClinikoMeta(null);
            setIntegrationSource(meta?.integrationSource || null);
        }
        setPatientDialogOpen(false);
        setLoading(true);

        // Proceed to email dialog with patient details stored
        if (isAuthExpiredDialogVisible()) {
            setLoading(false);
            return;
        }
        setTimeout(() => {
            setDialogOpen(true);
            setLoading(false);
        }, 200);
    };

    const prefillFromCliniko = async () => {
        const patientId = getClinikoPatientIdFromStorage();
        const hasClinikoKey = Boolean(userDataStore?.userData?.integrations?.cliniko?.apiKey);

        if (!patientId || !hasClinikoKey) {
            setClinikoPatient(null);
            setClinikoAppointments([]);
            return { patient: null, appointments: [] };
        }

        setClinikoLoading(true);
        setSpinnerMessage(INTEGRATION_SPINNER_MESSAGES.cliniko);
        try {
            const patient = await ClinikoApi.getPatientDetails(authStore.idToken, patientId);
            const appointmentsResponse = await ClinikoApi.getPatientAppointments(authStore.idToken, patientId);
            const appointments = Array.isArray(appointmentsResponse?.appointments)
                ? appointmentsResponse.appointments
                : [];
            setClinikoPatient(patient);
            setClinikoAppointments(appointments);
            return { patient, appointments };
        } catch (e) {
            console.warn('[Cliniko] Failed to prefill patient data:', e?.message || e);
            setClinikoPatient(null);
            setClinikoAppointments([]);
            return { patient: null, appointments: [] };
        } finally {
            setClinikoLoading(false);
            setSpinnerMessage('');
        }
    };

    const handleClick = async () => {
        // Check auth expiration first
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        // Check Free/Student account BEFORE unsaved changes
        if (accountType === 'Free' || accountType === 'Student') {
            onUpgradeRequired();
            return;
        }

        const executeEmailAction = async () => {
            setLoading(true);
            try {
                const { patient: clinikoPatientData } = await prefillFromCliniko();
                const hasClinikoPatient = Boolean(clinikoPatientData?.clinikoPatientId);
                // Check if we should show patient dialog first (for "My Library" content)
                if (
                    hasClinikoPatient ||
                    (typeof shouldShowPatientDialog === 'function' ? shouldShowPatientDialog() : false)
                ) {
                    setPatientDialogOpen(true);
                } else {
                    // For "My Clinic" content, proceed directly to email dialog

                    setDialogOpen(true);
                }
            } catch (error) {
                console.error('Error prefilling from ProVet:', error);
            } finally {
                setLoading(false);
            }
        };

        // Check for unsaved changes before proceeding
        if (hasUnsavedChanges) {
            promptBeforeAction(
                executeEmailAction,
                "Your changes haven't been saved. Do you want to save before sharing?",
            );
        } else {
            await executeEmailAction();
        }
    };

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingPatientDetails(null);
                    setClinikoMeta(null);
                    setIntegrationSource(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
                clinikoPatient={clinikoPatient}
                clinikoAppointments={clinikoAppointments}
                clinikoLoading={clinikoLoading}
            />
            <BlockingSpinner
                open={loading && !patientDialogOpen && !dialogOpen && !openSuccessDialog}
                message={spinnerMessage}
            />
            <CustomToolbarButton
                iconSrc={ASEmailIcon}
                tooltipTitle={bundledHandoutsView ? 'Email handouts' : 'Email handout'}
                onClick={handleClick}
            />

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                onConfirm={handleDialogSend}
                title="Send via email"
                message="Please enter your email address."
                confirmText="Send"
                cancelText="Cancel"
                cancelTextReq={true}
                showTextField={true}
                textFieldLabel="Email address"
                textFieldValue={email}
                onTextFieldChange={(e) => setEmail(e.target.value)}
                validate={ValidationUtils.validateEmail}
            />

            <SuccessDialog
                open={openSuccessDialog}
                handleClose={() => {
                    setOpenSuccessDialog(false);
                    setClinikoNoteStatus('idle');
                    setIntegrationSource(null);
                }}
                title="Email sent"
                message={
                    clinikoNoteStatus === 'added'
                        ? 'The email was sent successfully. A Cliniko note was added to the appointment.'
                        : clinikoNoteStatus === 'forbidden'
                          ? 'The email was sent successfully, but the Cliniko note could not be added due to your Cliniko permissions.'
                          : 'The email was sent successfully.'
                }
            />
            <ErrorDialog
                open={openErrorDialog}
                handleClose={() => {
                    setOpenErrorDialog(false);
                    setShowReloadAction(false);
                }}
                title={errorDialogTitle}
                message={errorDialogMessage}
                primaryActionText={showReloadAction ? 'Reload Page' : null}
                onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
            />
        </>
    );
}

export default HandoutEmailButton;
